import React from 'react';
import { Card, CardContent, CardMedia, CardActions, Typography, Button, Box, Tooltip, CircularProgress, IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { mdiInformationOutline } from '@mdi/js';
import Icon from '@mdi/react';
import theme from '../../theme';
import { SxProps, Theme } from '@mui/material';

export interface HomePageCardAction {
  label: string;
  route?: string;
  disabled?: boolean;
  informationMessage?: string;
  informationIcon?: string;
  informationIconColor?: string;
  loading?: boolean;
  variant?: 'contained' | 'outlined' | 'text';
}

export interface HomePageCardProps {
  title: string;
  description: string;
  image: string;
  imageAlt?: string;
  actions: HomePageCardAction[];
}

export const HomePageCard: React.FC<HomePageCardProps> = ({
  title,
  description,
  image,
  imageAlt,
  actions
}) => {
  const navigate = useNavigate();

  const handleActionClick = (route?: string) => {
    if (!route) return;
    route.startsWith('/')
      ? navigate(route)
      : window.open(route, '_blank');
  };

  return (
    <Card className="homepage-card">
      <CardMedia
        component="img"
        image={image}
        alt={imageAlt || title}
        className="homepage-card-media"
      />
      <CardContent className="homepage-card-content">
        <Box display="flex" alignItems="center" gap={1}>
          <Typography gutterBottom variant="h5" component="div" sx={{ mb: 0 }}>
            {title}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {description}
        </Typography>
      </CardContent>
      <CardActions disableSpacing className="homepage-card-actions" sx={{ background: theme.palette.primary.light, flexDirection: 'column', gap: 1 }}>
        {actions.map((action, index) => (
          <Box key={index} display="flex" alignItems="center" width="100%">
            <Button
              variant={action.variant ?? 'contained'}
              color="primary"
              fullWidth
              size="large"
              onClick={() => handleActionClick(action.route)}
              disabled={action.disabled || action.loading}
            >
              {action.loading
                ? <CircularProgress size={24} color="inherit" />
                : action.label
              }
            </Button>
            <Box
              sx={{
                color: action.informationIconColor ?? 'error.main',
                display: 'inline-flex',
                alignItems: 'center',
                flexShrink: 0,
                visibility: action.informationMessage ? 'visible' : 'hidden',
              } as SxProps<Theme>}
            >
              {action.informationMessage && (
                <Tooltip title={action.informationMessage} arrow placement="top">
                  <IconButton
                    size="small"
                    aria-label={action.informationMessage}
                    sx={{ color: 'inherit', padding: '4px' }}
                  >
                    <Icon path={action.informationIcon ?? mdiInformationOutline} size={1} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        ))}
      </CardActions>
    </Card>
  );
};
